import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  addHoursToLocalDateTime,
  hoursBetweenLocalDateTimes,
} from "../../../api/projectPlan";

const PRESET_HOURS = [1, 2, 3, 4, 5, 6, 7, 8];

const isWholeHourPreset = (hours) =>
  hours != null &&
  hours >= 1 &&
  hours <= 8 &&
  Math.abs(hours - Math.round(hours)) < 0.01;

/**
 * Start time + duration (preset buttons 1–8 h and manual hours) with optional end time field.
 */
export const ProjectEventResourceHours = ({
  startTime,
  endTime,
  onTimesChange,
  showEndTime = true,
}) => {
  const { t } = useTranslation();

  const durationHours = useMemo(
    () => hoursBetweenLocalDateTimes(startTime, endTime),
    [startTime, endTime]
  );

  const activePreset = isWholeHourPreset(durationHours)
    ? Math.round(durationHours)
    : null;

  const applyDuration = (hours) => {
    if (!startTime || hours == null || Number(hours) <= 0) return;
    onTimesChange({ end_time: addHoursToLocalDateTime(startTime, hours) });
  };

  const handleStartChange = (value) => {
    const keepHours = durationHours && durationHours > 0 ? durationHours : 8;
    if (value) {
      onTimesChange({
        start_time: value,
        end_time: addHoursToLocalDateTime(value, keepHours),
      });
    } else {
      onTimesChange({ start_time: value });
    }
  };

  const handleManualHoursChange = (raw) => {
    if (raw === "" || raw == null) return;
    const parsed = parseFloat(String(raw).replace(",", "."));
    if (!Number.isNaN(parsed) && parsed > 0) {
      applyDuration(parsed);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          {t("project.event.resource_start_time")} *
        </label>
        <input
          type="datetime-local"
          value={startTime || ""}
          onChange={(e) => handleStartChange(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-medium text-gray-600">
          {t("project.event.resource_hours")}
        </label>
        <div className="flex flex-wrap gap-1.5">
          {PRESET_HOURS.map((h) => (
            <button
              key={h}
              type="button"
              disabled={!startTime}
              onClick={() => applyDuration(h)}
              className={`min-w-[2.25rem] rounded-lg border px-2.5 py-1.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                activePreset === h
                  ? "border-teal-600 bg-teal-600 text-white"
                  : "border-slate-200 bg-white text-slate-800 hover:border-teal-400 hover:bg-teal-50"
              }`}
            >
              {h}
            </button>
          ))}
          <span className="self-center px-0.5 text-xs text-slate-500">
            {t("project.event.resource_hours_unit")}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <label
            htmlFor="resource-hours-manual"
            className="shrink-0 text-xs text-slate-600"
          >
            {t("project.event.resource_hours_manual")}
          </label>
          <input
            id="resource-hours-manual"
            type="number"
            min="0.25"
            max="24"
            step="0.25"
            disabled={!startTime}
            value={durationHours != null ? durationHours : ""}
            onChange={(e) => handleManualHoursChange(e.target.value)}
            className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
          />
        </div>
        {!startTime ? (
          <p className="mt-1 text-xs text-slate-500">
            {t("project.event.resource_hours_pick_start")}
          </p>
        ) : null}
      </div>

      {showEndTime ? (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("project.event.resource_end_time")} *
          </label>
          <input
            type="datetime-local"
            value={endTime || ""}
            onChange={(e) => onTimesChange({ end_time: e.target.value })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      ) : null}
    </div>
  );
};
